<template>
	<div
		v-if="!expandable"
		class="accordion-item static"
		:class="{ 'has-active-filters': hasActiveFilters || activeCount > 0 }"
	>
		<div class="accordion-header">
			<span class="accordion-legend">
				<slot name="title">{{ title }}</slot>
			</span>
			<span v-if="activeCount" class="active-count-badge">{{ activeCount }}</span>
			<slot name="header-actions" />
		</div>
	</div>

	<Accordion.Item
		v-else
		:value="value"
		class="accordion-item"
		:class="{ 'has-active-filters': hasActiveFilters || activeCount > 0 }"
	>
		<div class="accordion-header">
			<Accordion.ItemTrigger class="accordion-trigger">
				<Accordion.ItemIndicator class="accordion-icon">
					<span class="icon-chevron">
						<AppIcon :name="IconName.ChevronDown" :size="16" :stroke-width="1.2" />
					</span>
				</Accordion.ItemIndicator>
				<span class="accordion-legend">
					<slot name="title">{{ title }}</slot>
				</span>
				<span v-if="activeCount" class="active-count-badge">{{ activeCount }}</span>
			</Accordion.ItemTrigger>

			<slot name="header-actions" />
		</div>

		<Accordion.ItemContent class="accordion-content-wrapper">
			<div class="accordion-content">
				<slot />
			</div>
		</Accordion.ItemContent>
	</Accordion.Item>
</template>

<script setup lang="ts">
import { Accordion } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"

interface Props {
	value: string
	title?: string
	hasActiveFilters?: boolean
	activeCount?: number
	expandable?: boolean
}

withDefaults(defineProps<Props>(), {
	hasActiveFilters: false,
	activeCount: 0,
	expandable: true,
})
</script>

<style scoped>
.accordion-item {
	display: flex;
	flex-direction: column;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	background: var(--neutral-0);
	box-shadow: var(--shadow-sm);
	overflow: hidden;

	.accordion-header {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
		padding-right: var(--spacing-md);
	}

	&.static .accordion-header {
		min-height: 44px;
		padding: var(--spacing-sm) var(--spacing-md);
		gap: var(--spacing-sm);

		.accordion-legend {
			flex: 1;
			min-width: 0;
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-regular);
			color: var(--neutral-900);
		}
	}

	.accordion-trigger {
		flex: 1;
		min-width: 0;
		display: flex;
		justify-content: flex-start;
		align-items: center;
		gap: var(--spacing-sm);
		margin: 0;
		padding: var(--spacing-sm) var(--spacing-md);
		background: var(--neutral-0);
		border: none;
		cursor: pointer;
		text-align: left;
		transition: background-color 0.2s ease;
		min-height: 44px;

		&:hover,
		&:focus {
			background-color: var(--neutral-0);
			outline: none;
		}

		.accordion-legend {
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-regular);
			color: var(--neutral-900);
			margin: 0;
			padding: 0;
		}

		.accordion-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--size-icon-lg);
			height: var(--size-icon-lg);
			color: var(--neutral-600);
			transition: all 0.2s ease;
			flex-shrink: 0;

			.icon-chevron {
				display: flex;
				align-items: center;
				justify-content: center;
				transform: rotate(-90deg);
				transition: transform 0.2s ease;

				svg {
					align-items: center;
					display: block;
					transform-origin: center;
				}
			}
		}

		&:hover .accordion-icon {
			color: var(--primary-green);
		}

		.active-count-badge {
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--space-14);
			height: var(--space-14);
			border-radius: var(--border-radius-full);
			background: var(--neutral-100);
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			flex-shrink: 0;
		}
	}

	.accordion-content-wrapper {
		overflow: hidden;
		margin: 0;
		background-color: var(--neutral-0);

		&[data-state="open"] {
			height: var(--height);
			animation: accordion-slide-down 0.15s ease-out;
		}

		&[data-state="closed"] {
			height: 0;
			animation: accordion-slide-up 0.15s ease-out;
		}

		.accordion-content {
			padding: var(--spacing-md) var(--spacing-md) 0;
			border-top: var(--border-width-thin) solid var(--neutral-200);
			background-color: var(--neutral-0);
		}
	}

	&[data-state="open"] .accordion-trigger .accordion-icon .icon-chevron {
		transform: rotate(0deg);
	}

	&[data-state="open"] .accordion-trigger {
		background-color: var(--neutral-0);
	}
}

@keyframes accordion-slide-down {
	from {
		height: 0;
	}
	to {
		height: var(--height);
	}
}

@keyframes accordion-slide-up {
	from {
		height: var(--height);
	}
	to {
		height: 0;
	}
}
</style>
